import { useEffect, useId, useRef, useState } from 'react';
import {
  applyTheme,
  DEFAULT_PREFERENCE,
  readPreference,
  subscribePreferenceChange,
  writePreference,
  type ThemePreference,
} from '../theme';
import { useIsDarkTheme } from '../hooks/useIsDarkTheme';

/**
 * Hawk's header theme picker.
 *   Mode:          System / Light / Dark
 *   Event Colors:  the viewer's "readable" variant (distinct per-role
 *                  backgrounds inside transcripts)
 *
 * Rendered only on Hawk's own routes (mounted from Layout). On /eval-set/* and
 * /scan/* the embedded viewer renders its OWN picker; the pickers are
 * route-disjoint and never on screen together. Each persists to the
 * localStorage key for its route (see theme.ts) and then calls
 * `window.__APPLY_BROWSER_THEME__`, so all of them drive <html>'s
 * data-bs-theme through the same applyTheme path.
 *
 * This is a deliberate visual match of the viewer's ThemeToggle
 * (`@tsmono/inspect-components`). We can't reuse that component here: it pulls
 * in the viewer's global CSS (a CSS module + PopOver styles) and renders its
 * icon from the Bootstrap Icons *font* — all of which Hawk lazy-loads only on
 * the viewer routes, never on its own chrome. The ONLY intended differences:
 *   - icon delivered as inline SVG (Hawk's convention; same moon-stars-fill /
 *     sun-fill glyphs copied verbatim from Bootstrap Icons) rather than a font
 *     glyph, so no icon-font dependency on chrome routes;
 *   - colors via Tailwind in Hawk's palette (the trigger lives in Hawk's
 *     colored header; the menu and the Reset link follow Hawk's link/surface
 *     tokens) rather than the viewer's `--inspect-*` design tokens.
 * Structure, labels, Reset behavior, and Event-Colors semantics match exactly.
 *
 * No explicit ARIA role on the popover: it's not a modal `dialog`, and it isn't
 * a `menu` either (menus don't traditionally contain radio groups with a
 * checkbox). The native <fieldset>/<legend> plus labelled checkbox carry the
 * screen reader.
 */
export function ThemePicker() {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [pref, setPref] = useState<ThemePreference>(() => readPreference());
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  const commit = (next: ThemePreference) => {
    writePreference(next);
    setPref(next);
    applyTheme();
  };

  // Open/close lifecycle: close on Escape (return focus to trigger),
  // outside pointerdown, or focus moving outside the popover+trigger.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || buttonRef.current?.contains(t)) return;
      setOpen(false);
    };
    // Closes when Tab moves focus outside the popover (e.g. into UserMenu).
    // Without this, Tab-out would leave the popover open behind the new
    // focused element — looks broken even though it's technically fine.
    const onFocusIn = (e: FocusEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || buttonRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, [open]);

  // Pull in preference changes made in another tab (mode / event colors). The
  // resolved dark/light is handled separately by useIsDarkTheme, so this only
  // needs to cover the stored preference, not OS color-scheme flips.
  useEffect(
    () => subscribePreferenceChange(() => setPref(readPreference())),
    []
  );

  // Resolved dark/light from the applied [data-bs-theme] — re-renders the icon
  // on an OS flip (mode "system") without the picker tracking that itself.
  const isDark = useIsDarkTheme();
  const triggerLabel = `Theme: ${pref.mode}${
    pref.eventColors ? ', event colors on' : ''
  }`;

  return (
    <div className="relative inline-flex">
      <button
        ref={buttonRef}
        type="button"
        aria-label={triggerLabel}
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center justify-center rounded-md p-1.5 text-white/90 hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/30"
      >
        {isDark ? <MoonStarsIcon /> : <SunIcon />}
      </button>
      {open && (
        <div
          ref={popRef}
          className="absolute top-full right-0 mt-1 z-50 min-w-44 rounded-md px-2 py-1.5 shadow-lg bg-white border border-gray-200 text-gray-900 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100"
        >
          <div className="flex items-center justify-between gap-5 border-b border-gray-200 dark:border-gray-700 pb-1 mb-1.5 text-xs">
            <b>Theme</b>
            <button
              type="button"
              onClick={() => commit({ ...DEFAULT_PREFERENCE })}
              className="p-0 bg-transparent border-0 cursor-pointer whitespace-nowrap text-brand-600 hover:underline dark:text-brand-300"
            >
              Reset
            </button>
          </div>
          <fieldset className="border-b border-gray-200 dark:border-gray-700 pb-1.5 mb-1.5">
            <legend className="sr-only">Theme mode</legend>
            {(['system', 'light', 'dark'] as const).map(m => {
              const inputId = `${id}-mode-${m}`;
              return (
                <div
                  key={m}
                  className="flex items-center gap-2 px-1 py-0.5 text-xs"
                >
                  <input
                    id={inputId}
                    type="radio"
                    name={`${id}-mode`}
                    value={m}
                    checked={pref.mode === m}
                    onChange={() => commit({ ...pref, mode: m })}
                    className="cursor-pointer"
                  />
                  <label htmlFor={inputId} className="cursor-pointer">
                    {capitalize(m)}
                  </label>
                </div>
              );
            })}
          </fieldset>
          <div className="flex items-center gap-2 px-1 py-0.5 text-xs">
            <input
              id={`${id}-event-colors`}
              type="checkbox"
              checked={pref.eventColors}
              onChange={e => commit({ ...pref, eventColors: e.target.checked })}
              className="cursor-pointer"
            />
            <label
              htmlFor={`${id}-event-colors`}
              className="cursor-pointer"
              title="Distinct backgrounds for user/assistant/tool messages in transcripts"
            >
              Event Colors
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

const capitalize = (s: string) => s[0].toUpperCase() + s.slice(1);

// Bootstrap Icons `sun-fill`, inlined verbatim (Hawk ships no icon font).
function SunIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M8 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8M8 0a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-1 0v-2A.5.5 0 0 1 8 0m0 13a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-1 0v-2A.5.5 0 0 1 8 13m8-5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1 0-1h2a.5.5 0 0 1 .5.5M3 8a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1 0-1h2A.5.5 0 0 1 3 8m10.657-5.657a.5.5 0 0 1 0 .707l-1.414 1.415a.5.5 0 1 1-.707-.708l1.414-1.414a.5.5 0 0 1 .707 0m-9.193 9.193a.5.5 0 0 1 0 .707L3.05 13.657a.5.5 0 0 1-.707-.707l1.414-1.414a.5.5 0 0 1 .707 0m9.193 2.121a.5.5 0 0 1-.707 0l-1.414-1.414a.5.5 0 0 1 .707-.707l1.414 1.414a.5.5 0 0 1 0 .707M4.464 4.465a.5.5 0 0 1-.707 0L2.343 3.05a.5.5 0 1 1 .707-.707l1.414 1.414a.5.5 0 0 1 0 .708" />
    </svg>
  );
}

// Bootstrap Icons `moon-stars-fill`, inlined verbatim (matches the viewer's
// dark-mode trigger glyph).
function MoonStarsIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M6 .278a.77.77 0 0 1 .08.858 7.2 7.2 0 0 0-.878 3.46c0 4.021 3.278 7.277 7.318 7.277q.792-.001 1.533-.16a.79.79 0 0 1 .81.316.73.73 0 0 1-.031.893A8.35 8.35 0 0 1 8.344 16C3.734 16 0 12.286 0 7.71 0 4.266 2.114 1.312 5.124.06A.75.75 0 0 1 6 .278" />
      <path d="M10.794 3.148a.217.217 0 0 1 .412 0l.387 1.162c.173.518.579.924 1.097 1.097l1.162.387a.217.217 0 0 1 0 .412l-1.162.387a1.73 1.73 0 0 0-1.097 1.097l-.387 1.162a.217.217 0 0 1-.412 0l-.387-1.162A1.73 1.73 0 0 0 9.31 6.593l-1.162-.387a.217.217 0 0 1 0-.412l1.162-.387a1.73 1.73 0 0 0 1.097-1.097zM13.863.099a.145.145 0 0 1 .274 0l.258.774c.115.346.386.617.732.732l.774.258a.145.145 0 0 1 0 .274l-.774.258a1.16 1.16 0 0 0-.732.732l-.258.774a.145.145 0 0 1-.274 0l-.258-.774a1.16 1.16 0 0 0-.732-.732l-.774-.258a.145.145 0 0 1 0-.274l.774-.258c.346-.115.617-.386.732-.732z" />
    </svg>
  );
}
